import { Typography, TextField, Stack, RadioGroup, FormControlLabel, Radio, Select, MenuItem, Slider, Autocomplete, createFilterOptions, Checkbox, ListItemText, Box, Chip, useTheme, useMediaQuery } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useFormikContext } from 'formik'
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import { ALLOWED_PROJECT_SCOPES, ALLOWED_PROJECT_TYPES, ALLOWED_SIZES, ALLOWED_UNIT_SYSTEMS, ALLOWED_VISIBILITY, NewProjectFormFields, SpaceType } from './NewProject'
import { styles } from './projectDetailsFormStyles'
import residentialIcon from '../../assets/images/icons/spaceType/residential.svg'
import officeIcon from '../../assets/images/icons/spaceType/office.svg'
import institutionIcon from '../../assets/images/icons/spaceType/institution.svg'
import othersIcon from '../../assets/images/icons/spaceType/others.svg'
import { formatRangeAsText } from 'utils/text'
import markerIconPng from "leaflet/dist/images/marker-icon.png"
import { Icon } from 'leaflet'
import { Coords, locationSuggestionsAtom } from 'store/atoms/NewProject/newProjectAtoms'
import { useAtom } from 'jotai'
import 'leaflet/dist/leaflet.css'
import './leafletStyles.css'

import { MapContainer, Marker, TileLayer, Popup } from 'react-leaflet'
import { Map, Marker as LeafletMarker } from 'leaflet'

type Props = {
  locationInputRef: MutableRefObject<HTMLInputElement | null>
}

const spaceTypes: { name: SpaceType, icon: string }[] = [
  { name: "residential", icon: residentialIcon },
  { name: "office", icon: officeIcon },
  { name: "institution", icon: institutionIcon },
  { name: "others", icon: othersIcon }
];

const ProjectDetailsForm = ({ locationInputRef }: Props) => {

  const { values, handleChange, setFieldValue, errors, touched, handleBlur } = useFormikContext<NewProjectFormFields>();

  const [suggestions] = useAtom(locationSuggestionsAtom);
  const [zoom] = useState();

  const theme = useTheme();
  const isLaptop = useMediaQuery(theme.breakpoints.up('md'));

  const defaultCoords: Coords = { lat: 12.933456, lng: 77.623273 }

  const [coords, setCoords] = useState(defaultCoords);

  const [map, setMap] = useState<Map | null>()
  // const map = useRef<Map>(null);
  const mapRef = useCallback((_map: Map) => {
    setMap(_map)
  }, [])

  // const locationInputRef = useRef(null);
  const [marker, setMarker] = useState<LeafletMarker | null>();
  const markerRef = useCallback((_marker: LeafletMarker) => {
    setMarker(_marker);
  }, []);

  const markerEventHandlers = {
    dragend() {
      if (!marker) return;

      // when marker moves, create a new field value with the given address and the new lat longs
      const newPosition = marker.getLatLng();
      if (!newPosition) return;
      setFieldValue('location', {
        ...values.location,
        coords: newPosition,
        id: `pi-random-id-${(new Date())}`
      })
    }
  }

  useEffect(() => {
    const newLatLnValues = values.location.coords;

    if (!newLatLnValues) {
      setCoords(defaultCoords);
      return;
    }

    setCoords(newLatLnValues)

    if (!map || !marker) return;

    map.setView(newLatLnValues)
    marker.setLatLng(newLatLnValues)
  }, [values.location])

  return (
    <Stack 
      justifyContent={"space-between"} 
      alignItems={{xs: "center", md: "flex-start"}} 
      spacing={1} 
      sx={{
        width: "100%",
        height: {
          md: "100%"
        },
        flexDirection: {
          xs: "column-reverse",
          md: "row"
        },
        gap: {
          xs: 4,
          md: 6
        }
    }}>
      <Stack sx={styles.leftFormContainer}>
        <Stack sx={styles.fieldContainer}>
          <Typography sx={styles.label}>Name <span style={{ color: 'red' }}>*</span></Typography>
          <TextField
            name='projectName'
            variant='standard'
            value={values.projectName}
            sx={styles.inputField}
            placeholder='Enter project name'
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.projectName && !!touched.projectName}
            helperText={errors.projectName}
          />
        </Stack>
        <Stack sx={styles.fieldContainer}>
          <Typography sx={styles.label}>Description</Typography>
          <TextField
            name='projectDescription'
            variant='standard'
            multiline
            placeholder='Enter project description'
            value={values.projectDescription}
            sx={styles.inputField}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.projectDescription && touched.projectDescription}
            helperText={errors.projectDescription}
          />
        </Stack>
        <Stack sx={styles.fieldContainer}>
          <Typography sx={styles.label}>Project Start Date</Typography>
          <DatePicker
            value={values.projectStartDate ? dayjs(values.projectStartDate) : null}
            onChange={(newValue) => {
              setFieldValue('projectStartDate', newValue ? newValue.toISOString() : '');
            }}
            slotProps={{
              textField: {
                variant: 'standard',
                sx: styles.inputField,
                placeholder: 'Select project start date'
              }
            }}
          />
        </Stack>
        <Stack sx={styles.fieldContainer}>
          <Typography sx={styles.label}>Location <span style={{ color: 'red' }}>*</span></Typography>
          <Autocomplete
            disablePortal
            options={suggestions}
            value={values.location}
            openOnFocus
            getOptionLabel={(option) => option.address}
            isOptionEqualToValue={(option, value) => (option.id === value.id)}
            onChange={(_, val, reason) => {
              if (reason !== "selectOption") {
                return;
              }
              setFieldValue('location', val)
            }}
            filterOptions={() => suggestions} // dont filter based off what you type but rather show all the suggestions, since we are already filtering based off mapbox api
            // name='location'
            // sx={{ maxHeight: '25rem' }}
            renderInput={(params) => (
              <TextField
                {...params}
                value={values.location.address}
                variant='standard'
                // ref={locationInputRef}
                inputRef={(ref) => {
                  locationInputRef.current = ref
                }}
                // sx={styles.inputField}
                onChange={(e) => setFieldValue('location', {
                  ...values.location,
                  address: e.target.value,
                  id: `pi-random-id-${(new Date())}`
                })}
                sx={styles.inputField}
              />
            )}
          />
        </Stack>
        <Stack sx={styles.fieldContainer}>
          <Typography sx={styles.label}>Space Type</Typography>
          <Stack
            direction={"row"}
            // spacing={3}
            // flexWrap={"wrap"}
            paddingTop={1}
            sx={{
              // width: "max-content",
              display: "flex",
              // gridTemplateColumns: "repeat(auto-fit, max-content)"
              flexDirection: "row",
              flexWrap: "wrap",
              gap: "1rem",
              flex: "1"
            }}
          >
            {spaceTypes.map((spaceType) => (
              <Stack spacing={.5} alignItems={"center"} sx={{
                cursor: "pointer",
                aspectRatio: "1/1",
                "&:hover": {
                  // opacity: 0.8
                  background: "#F5F5F533",
                },
                background: spaceType.name === values.spaceType ? "#f5f5f544" : "#F5F5F511",
                padding: ".5rem 1rem",
                borderRadius: "1rem",
              }}
                key={spaceType.name}
                onClick={() => setFieldValue("spaceType", spaceType.name)}
              >
                <img src={spaceType.icon} alt={spaceType.name} width={80} height={80} />
                <Typography sx={{
                  fontSize: ".875rem",
                  textTransform: "capitalize"
                }}>{spaceType.name}</Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
        <Stack sx={styles.fieldContainer}>
          <Typography sx={styles.label}>Project Type</Typography>
          {/* <Stack direction={"row"} spacing={2}>
            <RadioGroup
              name="projectType"
              value={values.projectType}
              onChange={handleChange}
              sx={{
                flexDirection: "row",
                gap: "1rem",
                flexWrap: "wrap",
                textTransform: "capitalize"
              }}
            >
              {ALLOWED_PROJECT_TYPES.map((type) => (
                <FormControlLabel value={type} key={type} control={<Radio />} label={type} />
              ))}
            </RadioGroup>
          </Stack> */}
          <Select
            sx={{
              marginTop: "1rem"
            }}
            value={values.projectType}
            onChange={handleChange}
            name="projectType"
          >
            {ALLOWED_PROJECT_TYPES.map(type => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </Stack>
        <Stack sx={styles.fieldContainer}>
          <Typography sx={styles.label}>Project Scope</Typography>
          {/* radio field */}
          <Stack direction={"row"} spacing={2}>
            {/* <RadioGroup
            name="projectScope"
            value={values.projectScope}
            onChange={handleChange}
            >
            {ALLOWED_PROJECT_SCOPES.map((scope) => (
              <FormControlLabel value={scope} key={scope} control={<Radio />} label={scope} />
              ))}
              </RadioGroup> */}
            <Select
              name="projectScopes"
              value={values.projectScopes}
              onChange={handleChange}
              variant='standard'
              renderValue={(scopes) => (
                <Box sx={{ display: "flex", gap: '.25rem', flexWrap: 'wrap' }}>
                  {scopes.map(scope => (
                    <Chip key={scope.toString()} label={scope} />
                  ))}
                </Box>
              )}
              multiple
              sx={styles.inputField}
            >
              {ALLOWED_PROJECT_SCOPES.map((scope) => (
                // <FormControlLabel value={scope} key={scope} control={<Radio />} label={scope} />
                <MenuItem key={scope} value={scope}>
                  <Stack direction={"row"} spacing={1} alignItems={"center"}>
                    <Checkbox checked={values.projectScopes.includes(scope)} />
                    <ListItemText primary={scope} />
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </Stack>

        <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
          <Typography sx={styles.label}>Preferred Unit System</Typography>
          <Select
            name='unitSystem'
            variant='standard'
            value={values.unitSystem}
            sx={{
              width: "max-content",
              paddingInline: "1rem"
            }}
            onChange={handleChange}
          >
            {ALLOWED_UNIT_SYSTEMS.map((unitSystem) => (
              <MenuItem value={unitSystem} key={unitSystem} sx={{
                textTransform: "capitalize"
              }}>{unitSystem}</MenuItem>
            ))}
          </Select>
        </Stack>
        <Stack sx={styles.fieldContainer}>
          <Typography sx={styles.label}>Size <span style={{ color: 'red' }}>*</span></Typography>
          <RadioGroup
            name='size'
            value={values.size}
            onChange={handleChange}
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: "1rem",
              flex: "1"
            }}
          >
            {Object.entries(ALLOWED_SIZES).map(([key, size]) => (
              <FormControlLabel
                sx={{
                  width: "max-content"
                }}
                value={key} key={key} control={<Radio />}
                label={
                  <Stack gap={"1"}>
                    <Typography>{size.label}</Typography>
                    <Typography sx={{
                      fontSize: '.75rem',
                      color: '#999'
                    }}>{formatRangeAsText(size.min, size.max, 'imperial', values.unitSystem)}</Typography>
                  </Stack>
                }
              />
            ))}
          </RadioGroup>
        </Stack>

        <Stack sx={styles.fieldContainer}>
          <Typography sx={styles.label}>Visibility <span style={{ color: 'red' }}>*</span></Typography>
          <RadioGroup
            name='visibility'
            value={values.visibility}
            onChange={handleChange}
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: "1rem",
              flex: "1"
            }}
          >
            {ALLOWED_VISIBILITY.map((visibility) => (
              <FormControlLabel
                sx={{
                  width: "max-content"
                }}
                value={visibility} 
                key={visibility} 
                control={<Radio />}
                label={
                  <Stack gap={"1"}>
                    <Typography>{visibility.charAt(0).toUpperCase() + visibility.slice(1)}</Typography>
                    <Typography sx={{
                      fontSize: '.75rem',
                      color: '#999'
                    }}>
                      {visibility === 'public' 
                        ? 'Visible to everyone' 
                        : 'Only visible to workspace members'}
                    </Typography>
                  </Stack>
                }
              />
            ))}
          </RadioGroup>
        </Stack>
      </Stack>
      <Stack
        justifyContent={"center"}
        alignItems={"center"}
        sx={{
          width: "100%",
          // width: "max-content",
          maxWidth: { xs: "100%", sm : "90%", md: "50vw" },
          // border: "solid 2px white",
          borderRadius: "20px",
          height: { md: "calc(100vh - 20rem)" },
          aspectRatio: {
            xs: "16/9",
            md: "none"
          },
          position: "relative",
          overflow: "hidden"
        }}
      >
        {
          isLaptop ? (
          <MapContainer
            ref={mapRef}
            center={defaultCoords}
            zoom={13}
            dragging
            style={{
              width: "inherit", 
              height: "inherit", 
              maxWidth: "inherit",
              position: "fixed",
            }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker
              position={coords}
              draggable
              ref={markerRef}
              icon={new Icon({ iconUrl: markerIconPng, iconSize: [25, 41], iconAnchor: [12.5, 41] })}
              eventHandlers={markerEventHandlers}
            >
              {/* <Popup>
                A pretty CSS3 popup. <br /> Easily customizable.
              </Popup> */}
            </Marker>
          </MapContainer>
          ) : (
          <Box sx={{
            width: "100%",
            height: "100%",
            position: "relative" // Changed from fixed to relative
          }}>
            <MapContainer
              ref={mapRef}
              center={defaultCoords}
              zoom={13}
              dragging
              style={{
                width: "100%",
                height: "100%",
                position: "absolute",
                inset: 0,
              }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker
                position={coords}
                draggable
                ref={markerRef}
                icon={new Icon({ iconUrl: markerIconPng, iconSize: [25, 41], iconAnchor: [12.5, 41] })}
                eventHandlers={markerEventHandlers}
              >
                {/* <Popup>
                  A pretty CSS3 popup. <br /> Easily customizable.
                </Popup> */}
              </Marker>
            </MapContainer>
          </Box>
          )
        }
      </Stack>
    </Stack>
  )
}

export default ProjectDetailsForm